import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CircleCheck, Filter, Lock, RefreshCw, Users } from 'lucide-react';
import type {
  OpsApiEndpointPermission,
  OpsPermissionRole,
  OpsPermissionRoleInfo,
  OpsUiRoutePermission,
} from '@nadoutong/contracts';
import {
  OPS_ROLE_DISPLAY_ORDER,
  opsPermissionAllowsRole,
  opsPermissionIsAdminOnly,
} from '@nadoutong/contracts';
import { useOpsPermissions } from '../api/permissions.ts';
import { formatLocalTimestamp } from '../components/formatters.ts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  Button,
  buttonClass,
  Chip,
  Input,
  KpiCard,
  PageHeader,
  Panel,
  TBody,
  Table,
  TableEmpty,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

type PermissionMode = 'all' | 'admin-only' | 'shared';

const MODE_OPTIONS: ReadonlyArray<{
  readonly mode: PermissionMode;
  readonly labelKey: I18nKey;
}> = [
  { mode: 'all', labelKey: 'permissions.mode.all' },
  { mode: 'admin-only', labelKey: 'permissions.mode.admin_only' },
  { mode: 'shared', labelKey: 'permissions.mode.shared' },
];

export function OpsPermissionsPage(): JSX.Element {
  const t = useT();
  const permissions = useOpsPermissions();
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<PermissionMode>('all');

  const data = permissions.data;
  const filteredUiRoutes = useMemo(
    () => filterPermissions(data?.ui_routes ?? [], search, mode),
    [data?.ui_routes, mode, search],
  );
  const filteredApiEndpoints = useMemo(
    () => filterPermissions(data?.api_endpoints ?? [], search, mode),
    [data?.api_endpoints, mode, search],
  );
  const adminOnlyEndpointCount = useMemo(
    () =>
      (data?.api_endpoints ?? []).filter((row) =>
        opsPermissionIsAdminOnly(row.requiredRoles),
      ).length,
    [data?.api_endpoints],
  );

  return (
    <section className="flex flex-col gap-6" data-testid="ops-permissions-page">
      <div className="flex flex-col gap-2">
        <PageHeader
          title={t('permissions.title')}
          sub={t('permissions.subtitle')}
          actions={
            <>
              <Link
                to="/governance/ops-users"
                className={buttonClass('secondary')}
                data-testid="ops-permissions-users-link"
              >
                <Users aria-hidden="true" className="h-4 w-4" />
                {t('permissions.action.ops_users')}
              </Link>
              <Button type="button" onClick={() => void permissions.refetch()}>
                <RefreshCw aria-hidden="true" className="h-4 w-4" />
                {t('common.refresh')}
              </Button>
            </>
          }
        />
        <p className="m-0 font-mono text-label text-paper-low" data-testid="ops-permissions-generated-at">
          {t('permissions.generated')}{' '}
          {data?.generated_at !== undefined
            ? formatLocalTimestamp(data.generated_at, { seconds: true })
            : t('permissions.generated.pending')}
        </p>
      </div>

      {permissions.isError ? (
        <p className="m-0 rounded-lg border border-bad-line bg-bad-dim p-4 text-body text-bad-bright" role="alert">
          {t('permissions.error')}
        </p>
      ) : null}

      <section className="grid gap-3 md:grid-cols-4" aria-label="Permission summary">
        <KpiCard label={t('permissions.summary.roles')} value={data?.roles.length ?? 0} />
        <KpiCard label={t('permissions.summary.ui_routes')} value={data?.ui_routes.length ?? 0} />
        <KpiCard
          label={t('permissions.summary.api_endpoints')}
          value={data?.api_endpoints.length ?? 0}
        />
        <KpiCard
          label={t('permissions.summary.admin_only_endpoints')}
          value={adminOnlyEndpointCount}
        />
      </section>

      <Panel caption={t('permissions.role_legend')} padded aria-label="Role legend">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {(data?.roles ?? []).map((role) => (
            <RoleLegendCard key={role.role} role={role} />
          ))}
          {permissions.isLoading ? (
            <p className="m-0 text-body text-paper-low" data-testid="ops-permissions-loading">
              {t('permissions.loading')}
            </p>
          ) : null}
        </div>
      </Panel>

      <section
        className="rounded-lg border border-line-soft bg-card p-4"
        aria-label="Permission filters"
      >
        <div className="grid gap-4 xl:grid-cols-[minmax(260px,1fr)_auto] xl:items-end">
          <label className="flex flex-col gap-1.5">
            <span className="text-label text-paper-mid">{t('permissions.filter.label')}</span>
            <span className="relative">
              <Filter
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-paper-low"
              />
              <Input
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
                placeholder={t('permissions.filter.placeholder')}
                data-testid="ops-permissions-filter"
                className="pl-10"
              />
            </span>
          </label>
          <div className="inline-flex items-center gap-1 self-end rounded-lg border border-line bg-well p-1">
            {MODE_OPTIONS.map((option) => (
              <Button
                key={option.mode}
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setMode(option.mode)}
                data-testid={`ops-permissions-mode-${option.mode}`}
                className={mode === option.mode ? 'bg-active text-paper' : undefined}
              >
                {t(option.labelKey)}
              </Button>
            ))}
          </div>
        </div>
      </section>

      <PermissionTable
        title={t('permissions.table.ui_routes')}
        rows={filteredUiRoutes}
        kind="ui"
      />
      <PermissionTable
        title={t('permissions.table.api_endpoints')}
        rows={filteredApiEndpoints}
        kind="api"
      />
    </section>
  );
}

function RoleLegendCard({ role }: { readonly role: OpsPermissionRoleInfo }): JSX.Element {
  const t = useT();
  return (
    <article className="rounded-lg bg-well p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="m-0 text-body font-medium text-paper">{roleLabel(role.role, t)}</h3>
          <p className="m-0 mt-1 font-mono text-label text-paper-low">{role.role}</p>
        </div>
        <Chip tone={role.assignable ? 'signal' : 'neutral'} lamp={false}>
          {role.assignable ? t('permissions.role.assignable') : t('permissions.role.bootstrap')}
        </Chip>
      </div>
      <p className="m-0 mt-3 text-dense text-paper-mid">{roleNote(role.role, t)}</p>
      <p className="m-0 mt-3 text-dense text-paper-low">
        {t('permissions.role.inherits')}{' '}
        {role.inheritedRoles.length > 0
          ? role.inheritedRoles.map((inherited) => roleShortLabel(inherited, t)).join(', ')
          : t('permissions.role.inherits_none')}
      </p>
    </article>
  );
}

function PermissionTable({
  title,
  rows,
  kind,
}: {
  readonly title: string;
  readonly rows: readonly (OpsUiRoutePermission | OpsApiEndpointPermission)[];
  readonly kind: 'ui' | 'api';
}): JSX.Element {
  const t = useT();
  return (
    <Panel caption={title} count={rows.length} aria-label={title}>
      <Table testId={`ops-permissions-${kind}-table`} className="rounded-none border-0">
        <THead>
          <Tr interactive={false}>
            {kind === 'api' ? <Th>{t('permissions.column.method')}</Th> : null}
            <Th>{t('permissions.column.path')}</Th>
            <Th>{t('permissions.column.category')}</Th>
            <Th>{t('permissions.column.operation')}</Th>
            <Th>{t('permissions.column.allowed_roles')}</Th>
            {OPS_ROLE_DISPLAY_ORDER.map((role) => (
              <Th key={role} className="text-center">
                {roleShortLabel(role, t)}
              </Th>
            ))}
          </Tr>
        </THead>
        <TBody>
          {rows.map((row) => (
            <PermissionRow key={row.id} row={row} kind={kind} />
          ))}
          {rows.length === 0 ? (
            <TableEmpty colSpan={kind === 'api' ? 12 : 11}>
              {t('permissions.table.empty')}
            </TableEmpty>
          ) : null}
        </TBody>
      </Table>
    </Panel>
  );
}

function PermissionRow({
  row,
  kind,
}: {
  readonly row: OpsUiRoutePermission | OpsApiEndpointPermission;
  readonly kind: 'ui' | 'api';
}): JSX.Element {
  const apiRow = kind === 'api' && 'method' in row ? row : null;
  const t = useT();
  return (
    <Tr data-testid={`ops-permissions-row-${row.id}`}>
      {apiRow !== null ? (
        <Td>
          <span className="rounded bg-raised px-2 py-0.5 font-mono text-micro font-semibold text-paper">
            {apiRow.method}
          </span>
        </Td>
      ) : null}
      <Td mono emphasis>
        {row.path}
      </Td>
      <Td>{row.category}</Td>
      <Td emphasis>{row.operation}</Td>
      <Td>{rolesSummary(row.requiredRoles, t)}</Td>
      {OPS_ROLE_DISPLAY_ORDER.map((role) => (
        <Td key={role} className="text-center">
          <AllowedIndicator role={role} requiredRoles={row.requiredRoles} />
        </Td>
      ))}
    </Tr>
  );
}

function AllowedIndicator({
  role,
  requiredRoles,
}: {
  readonly role: OpsPermissionRole;
  readonly requiredRoles: readonly OpsPermissionRole[];
}): JSX.Element {
  const allowed = opsPermissionAllowsRole(role, requiredRoles);
  const label = allowed ? `${role} allowed` : `${role} denied`;
  return (
    <span className="inline-flex items-center justify-center" aria-label={label} title={label}>
      {allowed ? (
        <CircleCheck aria-hidden="true" className="h-4 w-4 text-ok" />
      ) : (
        <Lock aria-hidden="true" className="h-4 w-4 text-paper-low" />
      )}
    </span>
  );
}

function filterPermissions<T extends OpsUiRoutePermission | OpsApiEndpointPermission>(
  rows: readonly T[],
  search: string,
  mode: PermissionMode,
): readonly T[] {
  const query = search.trim().toLowerCase();
  return rows.filter((row) => {
    if (mode === 'admin-only' && !opsPermissionIsAdminOnly(row.requiredRoles)) {
      return false;
    }
    if (mode === 'shared' && opsPermissionIsAdminOnly(row.requiredRoles)) {
      return false;
    }
    if (query.length === 0) {
      return true;
    }
    const method = 'method' in row ? row.method : '';
    return [method, row.path, row.category, row.operation, row.requiredRoles.join(' ')]
      .join(' ')
      .toLowerCase()
      .includes(query);
  });
}

function rolesSummary(
  requiredRoles: readonly OpsPermissionRole[],
  t: (key: I18nKey, params?: Record<string, string | number>) => string,
): string {
  if (requiredRoles.length === 0) {
    return t('permissions.roles.all_authenticated');
  }
  return requiredRoles.map((role) => roleShortLabel(role, t)).join(', ');
}

function roleShortLabel(
  role: OpsPermissionRole,
  t: (key: I18nKey, params?: Record<string, string | number>) => string,
): string {
  switch (role) {
    case 'ops-super-admin':
      return t('permissions.role.short.super');
    case 'ops-admin':
      return t('permissions.role.short.admin');
    case 'ops-support':
      return t('permissions.role.short.support');
    case 'ops-customer-manager':
      return t('permissions.role.short.customer');
    case 'ops-finance-manager':
      return t('permissions.role.short.finance');
    case 'ops-supply-demand-manager':
      return t('permissions.role.short.supply');
    case 'ops-runtime-operator':
      return t('permissions.role.short.runtime');
    default:
      return role;
  }
}

function roleLabel(
  role: OpsPermissionRole,
  t: (key: I18nKey, params?: Record<string, string | number>) => string,
): string {
  switch (role) {
    case 'ops-super-admin':
      return t('permissions.role.label.super');
    case 'ops-admin':
      return t('permissions.role.label.admin');
    case 'ops-support':
      return t('permissions.role.label.support');
    case 'ops-customer-manager':
      return t('permissions.role.label.customer');
    case 'ops-finance-manager':
      return t('permissions.role.label.finance');
    case 'ops-supply-demand-manager':
      return t('permissions.role.label.supply');
    case 'ops-runtime-operator':
      return t('permissions.role.label.runtime');
    default:
      return role;
  }
}

function roleNote(
  role: OpsPermissionRole,
  t: (key: I18nKey, params?: Record<string, string | number>) => string,
): string {
  switch (role) {
    case 'ops-super-admin':
      return t('permissions.role.note.super');
    case 'ops-admin':
      return t('permissions.role.note.admin');
    case 'ops-support':
      return t('permissions.role.note.support');
    case 'ops-customer-manager':
      return t('permissions.role.note.customer');
    case 'ops-finance-manager':
      return t('permissions.role.note.finance');
    case 'ops-supply-demand-manager':
      return t('permissions.role.note.supply');
    case 'ops-runtime-operator':
      return t('permissions.role.note.runtime');
    default:
      return role;
  }
}
